/**
 * ClipEditState — the Studio iterate editor's typed override layer.
 *
 * A clip's edits are an ORDERED JOURNAL of typed patches (spec §Operator
 * additions #4: "step memory, full precision"). The effective preview props
 * are derived by replaying the journal over the render pipeline's own
 * inputProps (GET /api/clips/:id/studio-inputs), client-side, before they
 * reach @remotion/player:
 *
 *     effective = applyEditState(inputProps, state, livePatch?)
 *
 * Design rules:
 *   - The journal is the single source of truth. Undo = drop the last
 *     entry; replay gives the exact prior state. No shadow copies to drift.
 *   - Everything is JSON-serializable — the journal is the persistence AND
 *     chat substrate for Phases 3–4 (edit-state.json gains this shape, and
 *     the iterate endpoint's IteratePatch is EditPatch + explanation/scope).
 *   - Precision: values pass through untouched. The number the inspector
 *     shows IS the number the composition receives.
 *   - Baseline materialization: when a clip has no explicit layout/brand
 *     (legacy Quotologies defaults baked into the components), the FIRST
 *     touch of layout/brand materializes the full legacy baseline from the
 *     LEGACY_* constants in src/remotion/types.ts, then merges the patch —
 *     so editing one field never disturbs the others, and an untouched
 *     journal returns the props object IDENTICALLY (no re-render churn,
 *     no accidental prop drift).
 *
 * Pure module: no React, no browser APIs — unit-testable under root vitest
 * and reusable server-side in Phase 4 (edit-state.json persistence).
 */
import {
  CANVAS_HEIGHT,
  CANVAS_WIDTH,
  DEFAULT_VIDEO_DIM,
  LEGACY_CAPTION_LAYOUT,
  LEGACY_LOGO_LAYOUT,
  LEGACY_RENDER_BRAND,
  LEGACY_SPEAKER_CARD_LEFT_PX,
  LEGACY_SPEAKER_CARD_TOP_PX,
  type ClipProps,
  type RenderBrand,
  type RenderLayout,
  type SerializableCue,
} from "../remotion/types.ts";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Where a mutation came from. "chat" = the Studio iterate loop (Phase 3).
 *  "fit" = the measured fit gate split a cue to keep it inside the frame
 *  (a Studio design commit, or the render safety net — spec 2026-09-17). */
export type EditSource = "canvas" | "inspector" | "chat" | "fit";
export const EDIT_SOURCES: ReadonlySet<string> = new Set<EditSource>(["canvas", "inspector", "chat", "fit"]);

/** Concrete speaker-card placement (RenderLayout.speakerCard, non-nullable). */
export type SpeakerCardLayout = NonNullable<RenderLayout["speakerCard"]>;

/** Concrete footage treatment (RenderLayout.video, non-nullable). */
export type VideoLayout = NonNullable<RenderLayout["video"]>;

/** Timed text overlays as a patch carries them — the WHOLE array (replace
 *  semantics: there is no per-entry merge, an overlay is edited by resending
 *  the list). */
export type OverlaysLayout = NonNullable<RenderLayout["overlays"]>;

/**
 * THE UNSET CONVENTION (patch layer only — `RenderLayout` is untouched).
 *
 * A few layout keys are OPTIONAL in a way that matters: absent is not zero,
 * it means "the upstream default applies" (uncapped caption wrapping; the
 * still generator's own name size / role line). Going BACK to that default is
 * a real operator action, and `undefined` cannot express it: `JSON.stringify`
 * drops an undefined value, so the journal entry that meant "clear it"
 * vanishes the moment the state is persisted or posted to the server, and the
 * earlier explicit value silently wins again.
 *
 * `null` survives JSON. So a patch carries `null` for "unset", `applyEditState`
 * DELETES the key when the collapsed patch says null, and channel promotion
 * strips it so channel.json never holds a literal null.
 *
 * ONLY the keys listed in CAPTION_UNSETTABLE / SPEAKER_CARD_UNSETTABLE take
 * part. Every other null in a layout patch is a meaningful VALUE — `stroke`,
 * `box`, `karaoke`, `animation` and `twoLine` all use null to mean "off" —
 * and must never be stripped.
 */
export type CaptionPatch =
  Omit<Partial<RenderLayout["caption"]>, "maxLines"> & { maxLines?: 1 | 2 | 3 | null };

/** Speaker card as a PATCH carries it — `null` unsets the three keys the
 *  still generator (not the composition) consumes. See CaptionPatch. */
export type SpeakerCardPatch =
  Omit<Partial<SpeakerCardLayout>, "nameFontPx" | "durationSec" | "descriptionWords"> & {
    nameFontPx?: number | null;
    durationSec?: number | null;
    descriptionWords?: number | null;
  };

/** Caption keys whose `null` means UNSET (see CaptionPatch). */
export const CAPTION_UNSETTABLE = ["maxLines"] as const;
/** Speaker-card keys whose `null` means UNSET (see CaptionPatch). */
export const SPEAKER_CARD_UNSETTABLE = ["nameFontPx", "durationSec", "descriptionWords"] as const;

/**
 * `merged` minus every key the PATCH marked unset (`null`). Pure — returns a
 * copy. Every sink that hands a merged layout to something which reads
 * "absent = default" must go through this: the live preview
 * (`applyEditState`), channel promotion, and the card-typography override.
 *
 * Driven by the PATCH, never by the merged value, and limited to `keys`, so a
 * null that means "off" (stroke/box/karaoke/animation/twoLine) is untouched.
 * Pass the patch as both arguments to clean a patch in place of a merge.
 */
export function withoutUnset<T extends object>(
  merged: T,
  patch: object | undefined,
  keys: readonly string[],
): T {
  if (!patch) return { ...merged };
  const p = patch as Record<string, unknown>;
  const out = { ...merged } as Record<string, unknown>;
  for (const k of keys) if (p[k] === null) delete out[k];
  return out as T;
}

/** One cue-text rewrite. `index` is the 0-based position in ClipProps.cues;
 *  `text` may carry `\n` (two-line layout) and `<em>` tags (gold word). */
export interface CuePatch {
  index: number;
  text: string;
}

/** Split cue `index` into `parts.length` (≥2) sequential cues, dividing its
 *  time window proportionally to each part's length. `index` is interpreted
 *  against the cue list AS IT STANDS when this entry is replayed (cue ops
 *  apply in journal order — see applyCueOpsToList), so a later split's index
 *  reflects any earlier split. Each part is storage syntax (`\n` + `<em>`). */
export interface CueSplit {
  index: number;
  parts: string[];
}

/**
 * A typed, partial override — the unit of change. Field-level last-wins
 * when collapsed across the journal. Matches the spec's IteratePatch minus
 * the chat-only fields (explanation, scope) which Phase 3 layers on top.
 */
export interface EditPatch {
  layout?: {
    /** `maxLines: null` = unset (see CaptionPatch). */
    caption?: CaptionPatch;
    logo?: Partial<RenderLayout["logo"]>;
    /** `nameFontPx`/`durationSec`/`descriptionWords` accept `null` = unset. */
    speakerCard?: SpeakerCardPatch;
    video?: Partial<VideoLayout>;
    /** REPLACES the whole overlay list (no per-entry merge). */
    overlays?: OverlaysLayout;
  };
  brand?: Partial<RenderBrand>;
  cues?: CuePatch[];
  /** Structural cue splits (Tweak Mode: "max 2 lines → second caption"). */
  cueSplits?: CueSplit[];
}

/** One journal step: what changed, when, and from which input surface. */
export interface EditJournalEntry {
  /** Epoch ms at commit time. */
  ts: number;
  source: EditSource;
  /** Human one-liner shown in the step list (e.g. "Moved logo"). For chat
   *  patches this is the model's explanation — the readable step history. */
  label: string;
  patch: EditPatch;
  /**
   * Channel-scope lifecycle.
   *
   * "channel-pending" — the operator confirmed a chat patch as channel-wide
   * but it hasn't been lifted into the channel template yet. The patch still
   * applies only to THIS clip's edit state.
   *
   * "channel" — the promotion pass copied this entry's layout/brand values
   * into the channel config (`promotedAt` records when). The entry keeps
   * applying to this clip (idempotent — the channel baseline now carries the
   * same numbers), and the journal keeps the full audit trail.
   */
  scope?: "channel-pending" | "channel";
  /**
   * CUE-BAKE marker. Set when "Approve & render" wrote this entry's cue
   * rewrites into work/<clip>/arabic.srt. From then on the SRT is the truth
   * for those cues, so replay SKIPS the entry's `cues` (double-applying
   * would corrupt a later re-translation whose cue indices shifted). The
   * entry's layout/brand parts — if any — keep applying normally.
   */
  bakedAt?: number;
  /** When the promotion pass lifted this entry's layout/brand into the
   *  channel template. Paired with scope "channel". */
  promotedAt?: number;
}

/** The whole per-clip edit state. Serializable as-is (localStorage draft
 *  now; work/<clip>/edit-state.json in Phase 4). */
export interface ClipEditState {
  version: 1;
  journal: EditJournalEntry[];
}

// ---------------------------------------------------------------------------
// State ops
// ---------------------------------------------------------------------------

export function emptyEditState(): ClipEditState {
  return { version: 1, journal: [] };
}

/** Append a step (immutable). */
export function appendEntry(state: ClipEditState, entry: EditJournalEntry): ClipEditState {
  return { version: 1, journal: [...state.journal, entry] };
}

/** Undo = pop the last step; replay of the rest IS the prior state. */
export function undoLast(state: ClipEditState): ClipEditState {
  if (state.journal.length === 0) return state;
  return { version: 1, journal: state.journal.slice(0, -1) };
}

/** Parse a serialized ClipEditState (localStorage draft / edit-state.json).
 *  Returns null on anything malformed — callers fall back to empty state. */
export function parseEditState(json: string | null | undefined): ClipEditState | null {
  if (!json) return null;
  try {
    const v = JSON.parse(json) as unknown;
    if (typeof v !== "object" || v === null) return null;
    const s = v as Partial<ClipEditState>;
    if (s.version !== 1 || !Array.isArray(s.journal)) return null;
    for (const e of s.journal) {
      if (typeof e !== "object" || e === null) return null;
      const entry = e as Partial<EditJournalEntry>;
      if (typeof entry.ts !== "number") return null;
      if (typeof entry.source !== "string" || !EDIT_SOURCES.has(entry.source)) return null;
      if (typeof entry.label !== "string") return null;
      if (typeof entry.patch !== "object" || entry.patch === null) return null;
      if (entry.scope !== undefined && entry.scope !== "channel-pending" && entry.scope !== "channel") return null;
      if (entry.bakedAt !== undefined && typeof entry.bakedAt !== "number") return null;
      if (entry.promotedAt !== undefined && typeof entry.promotedAt !== "number") return null;
    }
    return { version: 1, journal: s.journal as EditJournalEntry[] };
  } catch {
    return null;
  }
}

/**
 * True when `candidate`'s journal extends `base`'s: every entry of `base`
 * is deep-equal to the corresponding entry of `candidate` (candidate may
 * have more). The Studio's load-time reconciliation: the server state wins
 * UNLESS the localStorage draft strictly extends it — then the draft is the
 * unsaved buffer ahead of the server and is pushed up by autosave. A local
 * draft that DIVERGED (another device edited, or approve added baked
 * markers) fails the prefix check and the server wins.
 */
export function journalExtends(base: ClipEditState, candidate: ClipEditState): boolean {
  if (candidate.journal.length < base.journal.length) return false;
  return base.journal.every(
    (e, i) => JSON.stringify(e) === JSON.stringify(candidate.journal[i]),
  );
}

// ---------------------------------------------------------------------------
// Phase 4 lifecycle — cue baking + channel promotion (pure transforms)
// ---------------------------------------------------------------------------

/** Collapsed cue rewrites from UNBAKED entries only, last-wins per index —
 *  exactly what "Approve & render" writes back into arabic.srt. */
export function unbakedCuePatches(state: ClipEditState): CuePatch[] {
  const collapsed = collapsePatches(
    state.journal.filter((e) => e.bakedAt === undefined).map((e) => e.patch),
  );
  return collapsed.cues ?? [];
}

/** Mark every entry that carries cue rewrites (and isn't already baked) as
 *  baked at `ts`. Called right after the SRT writeback so the journal keeps
 *  the audit trail without ever double-applying. Immutable. */
export function markCuesBaked(state: ClipEditState, ts: number): ClipEditState {
  return {
    version: 1,
    journal: state.journal.map((e) =>
      ((e.patch.cues && e.patch.cues.length > 0) || (e.patch.cueSplits && e.patch.cueSplits.length > 0))
      && e.bakedAt === undefined
        ? { ...e, bakedAt: ts }
        : e,
    ),
  };
}

/** True when an entry's patch touches layout or brand (the promotable part). */
export function touchesLayoutOrBrand(patch: EditPatch): boolean {
  const l = patch.layout;
  const layoutTouched =
    !!l && ((!!l.caption && Object.keys(l.caption).length > 0)
      || (!!l.logo && Object.keys(l.logo).length > 0)
      || (!!l.speakerCard && Object.keys(l.speakerCard).length > 0)
      || (!!l.video && Object.keys(l.video).length > 0)
      // [] counts: clearing the overlays is a promotable design change.
      || !!l.overlays);
  return layoutTouched || (!!patch.brand && Object.keys(patch.brand).length > 0);
}

/** The journal's accumulated DESIGN (layout + brand only, cues excluded),
 *  field-level last-wins — what channel promotion collapses. */
export function collapseLayoutBrand(state: ClipEditState): EditPatch {
  const collapsed = collapsePatches(state.journal.map((e) => e.patch));
  const out: EditPatch = {};
  if (collapsed.layout) out.layout = collapsed.layout;
  if (collapsed.brand) out.brand = collapsed.brand;
  return out;
}

/** True when the journal holds confirmed-channel chat patches that promotion
 *  hasn't lifted yet — drives the "save to channel too?" prompt at approve. */
export function hasPendingChannelEntries(state: ClipEditState): boolean {
  return state.journal.some((e) => e.scope === "channel-pending" && e.promotedAt === undefined);
}

/** Mark every not-yet-promoted entry that contributed layout/brand as
 *  promoted at `ts` ({scope:"channel"} + promotedAt). Immutable. */
export function markDesignPromoted(state: ClipEditState, ts: number): ClipEditState {
  return {
    version: 1,
    journal: state.journal.map((e) =>
      e.promotedAt === undefined && (touchesLayoutOrBrand(e.patch) || e.scope === "channel-pending")
        ? { ...e, scope: "channel" as const, promotedAt: ts }
        : e,
    ),
  };
}

// ---------------------------------------------------------------------------
// Baselines — the concrete values "absent" means
// ---------------------------------------------------------------------------

const round2 = (v: number): number => Math.round(v * 100) / 100;

/** Founding card placement expressed in the % coords speakerCard uses.
 *  Chosen so resolveCardPx round-trips to EXACTLY 130 / 320 px:
 *  round(6.02% · 2160) = 130, round(8.33% · 3840) = 320. */
/** The PLACEMENT half of SpeakerCardLayout — what the Studio inspector shows
 *  and what a drag edits. The card's timing/typography knobs (durationSec,
 *  nameFontPx, descriptionWords) are consumed UPSTREAM (the overlay scheduler
 *  and the still generator), never by the canvas, so they have no baseline to
 *  materialize here and stay optional pass-through. */
export type SpeakerCardPlacementLayout = Required<Pick<SpeakerCardLayout, "xPct" | "yPct" | "scale">>;

export const LEGACY_SPEAKER_CARD_PCT: SpeakerCardPlacementLayout = {
  xPct: round2((LEGACY_SPEAKER_CARD_LEFT_PX / CANVAS_WIDTH) * 100),
  yPct: round2((LEGACY_SPEAKER_CARD_TOP_PX / CANVAS_HEIGHT) * 100),
  scale: 1,
};

/** The COMPLETE layout a clip renders with: its explicit layout when the
 *  channel system provided one, else the founding legacy values — with the
 *  optional sub-parts (logo.xPct, speakerCard) made concrete. This is what
 *  the inspector displays and what drags start from. */
export function baselineLayout(props: ClipProps): Required<Pick<RenderLayout, "caption">> & {
  logo: Required<RenderLayout["logo"]>;
  speakerCard: SpeakerCardPlacementLayout;
  video: Required<VideoLayout>;
} {
  const l = props.layout;
  return {
    caption: l ? { ...l.caption } : { ...LEGACY_CAPTION_LAYOUT },
    logo: l ? { ...LEGACY_LOGO_LAYOUT, ...l.logo } : { ...LEGACY_LOGO_LAYOUT },
    speakerCard: l?.speakerCard
      ? { scale: 1, ...l.speakerCard }
      : { ...LEGACY_SPEAKER_CARD_PCT },
    // The deployment's dim materialized (inputProps.videoDim = QL_VIDEO_DIM,
    // else the founding constant) — value-identical to an absent video block,
    // since the composition resolves layout.video.dim ?? videoDim ?? DEFAULT.
    // So an unrelated layout edit that rebuilds props.layout cannot change a
    // pixel — materializing the founding 0.28 here would silently override a
    // 0.45 deployment on the first drag.
    // brightness/contrast materialize at their IDENTITY (1): the composition
    // emits no filter when both are 1, so a baseline-rebuilt layout renders
    // exactly as an absent video block.
    video: {
      dim: l?.video?.dim ?? props.videoDim ?? DEFAULT_VIDEO_DIM,
      brightness: l?.video?.brightness ?? 1,
      contrast: l?.video?.contrast ?? 1,
    },
  };
}

/** The COMPLETE brand a clip renders with (explicit channel brand with its
 *  optional fields defaulted, else the founding legacy brand). */
export function baselineBrand(props: ClipProps): Required<RenderBrand> {
  const base = LEGACY_RENDER_BRAND as Required<RenderBrand>;
  return props.brand ? { ...base, ...props.brand } : { ...base };
}

// ---------------------------------------------------------------------------
// Collapse + apply
// ---------------------------------------------------------------------------

/** Merge an ordered list of patches, field-level last-wins; cue rewrites
 *  last-win per index. */
export function collapsePatches(patches: EditPatch[]): EditPatch {
  const out: EditPatch = {};
  const cueByIdx = new Map<number, string>();
  for (const p of patches) {
    if (p.layout) {
      const lo = (out.layout ??= {});
      if (p.layout.caption) lo.caption = { ...lo.caption, ...p.layout.caption };
      if (p.layout.logo) lo.logo = { ...lo.logo, ...p.layout.logo };
      if (p.layout.speakerCard) lo.speakerCard = { ...lo.speakerCard, ...p.layout.speakerCard };
      if (p.layout.video) lo.video = { ...lo.video, ...p.layout.video };
      // Replace, never merge — the array IS the value.
      if (p.layout.overlays) lo.overlays = p.layout.overlays;
    }
    if (p.brand) out.brand = { ...out.brand, ...p.brand };
    if (p.cues) for (const c of p.cues) cueByIdx.set(c.index, c.text);
  }
  if (cueByIdx.size > 0) {
    out.cues = [...cueByIdx.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([index, text]) => ({ index, text }));
  }
  return out;
}

/**
 * The replayable patch of one journal entry. Identity for live (unbaked)
 * entries; entries whose cue rewrites were BAKED into the SRT replay with
 * `cues` stripped — the SRT already carries that text, and a later
 * re-translation may renumber cues, so re-applying by index would corrupt.
 */
export function replayablePatch(entry: EditJournalEntry): EditPatch {
  if (entry.bakedAt === undefined) return entry.patch;
  if (!entry.patch.cues && !entry.patch.cueSplits) return entry.patch;
  const { cues: _c, cueSplits: _s, ...rest } = entry.patch;
  return rest;
}

/** Divide one cue's time window across `parts` (≥2), weighted by each part's
 *  visible length (em tags stripped), returning sequential cues that tile
 *  [startMs, endMs] with no gap or overlap. Pure. */
export function splitCueIntoParts(cue: SerializableCue, parts: string[]): SerializableCue[] {
  if (parts.length < 2) return [{ startMs: cue.startMs, endMs: cue.endMs, text: parts[0] ?? cue.text }];
  const total = Math.max(1, cue.endMs - cue.startMs);
  const weights = parts.map((p) => Math.max(1, p.replace(/<\/?em[^>]*>/g, "").replace(/\s+/g, " ").trim().length));
  const sum = weights.reduce((a, b) => a + b, 0);
  const out: SerializableCue[] = [];
  let acc = 0;
  let t = cue.startMs;
  parts.forEach((text, i) => {
    acc += weights[i]!;
    const endMs = i === parts.length - 1 ? cue.endMs : Math.round(cue.startMs + (total * acc) / sum);
    out.push({ startMs: t, endMs: Math.max(t + 1, endMs), text });
    t = out[out.length - 1]!.endMs;
  });
  return out;
}

/**
 * Apply cue text-rewrites + splits from an ordered patch list to a cue list,
 * IN ORDER — each patch's `index` is read against the list as it stands at
 * that step. This is what keeps indices correct across a split (which changes
 * the cue count): an edit made after a split refers to post-split positions.
 * With no splits anywhere it is identical to the old collapse-by-index path.
 * Shared by the live preview (applyEditState) and the SRT bake.
 */
export function applyCueOpsToList(
  cues: SerializableCue[],
  patches: EditPatch[],
  /** Called with any op index that falls outside the cue list at its step —
   *  the bake logs these; the live preview ignores them (they simply no-op). */
  onSkip?: (index: number) => void,
): SerializableCue[] {
  let out: SerializableCue[] = cues;
  for (const p of patches) {
    if (p.cues && p.cues.length > 0) {
      if (onSkip) for (const c of p.cues) if (c.index < 0 || c.index >= out.length) onSkip(c.index);
      const byIdx = new Map(p.cues.map((c) => [c.index, c.text]));
      out = out.map((c, i) => (byIdx.has(i) ? { ...c, text: byIdx.get(i)! } : c));
    }
    if (p.cueSplits && p.cueSplits.length > 0) {
      if (onSkip) for (const s of p.cueSplits) if (s.index < 0 || s.index >= out.length) onSkip(s.index);
      const byIdx = new Map(p.cueSplits.map((s) => [s.index, s.parts]));
      const expanded: SerializableCue[] = [];
      out.forEach((c, i) => {
        const parts = byIdx.get(i);
        if (parts && parts.length >= 2) expanded.push(...splitCueIntoParts(c, parts));
        else expanded.push(c);
      });
      out = expanded;
    }
  }
  return out;
}

/**
 * The pure heart of the Studio: replay the journal (plus an optional
 * uncommitted live patch — mid-drag preview) over the render pipeline's
 * inputProps. Untouched state returns `props` IDENTICALLY (same reference).
 * Baked cue rewrites are skipped (see replayablePatch).
 */
export function applyEditState(
  props: ClipProps,
  state: ClipEditState,
  livePatch?: EditPatch | null,
): ClipProps {
  const patches = state.journal.map(replayablePatch);
  if (livePatch) patches.push(livePatch);
  // Layout/brand collapse (field-level last-wins); cues apply SEQUENTIALLY so
  // splits (which change the cue count) keep every index meaning "the cue at
  // that position at this step".
  const p = collapsePatches(patches);

  const touchesLayout = !!p.layout;
  const touchesBrand = !!p.brand;
  const touchesCues = patches.some((q) => (q.cues && q.cues.length > 0) || (q.cueSplits && q.cueSplits.length > 0));
  if (!touchesLayout && !touchesBrand && !touchesCues) return props;

  const next: ClipProps = { ...props };
  if (touchesLayout) {
    const base = baselineLayout(props);
    // A key the collapsed patch set to `null` is UNSET: drop it so the render
    // sees the upstream default again (see the UNSET CONVENTION above). Only
    // the listed keys — every other null in these blocks is "off", a value.
    // After this neither object holds a null on an unsettable key, which is
    // what makes the casts sound.
    const caption = withoutUnset(
      { ...base.caption, ...p.layout!.caption },
      p.layout!.caption,
      CAPTION_UNSETTABLE,
    );
    const speakerCard = withoutUnset(
      { ...base.speakerCard, ...p.layout!.speakerCard },
      p.layout!.speakerCard,
      SPEAKER_CARD_UNSETTABLE,
    );
    next.layout = {
      caption: caption as RenderLayout["caption"],
      logo: { ...base.logo, ...p.layout!.logo },
      speakerCard: speakerCard as NonNullable<RenderLayout["speakerCard"]>,
      video: { ...base.video, ...p.layout!.video },
      // Absent in the patch → keep whatever the clip already renders, so an
      // unrelated layout edit can never drop the brand's CTA overlay.
      // The patch wins whenever it mentions overlays (INCLUDING [] — that is
      // a clear). Silent on overlays → keep whatever the clip already renders,
      // so an unrelated layout edit can never drop the brand's CTA overlay.
      ...((): Pick<RenderLayout, "overlays"> | Record<string, never> => {
        const ov = p.layout!.overlays ?? props.layout?.overlays;
        return ov ? { overlays: ov } : {};
      })(),
    };
  }
  if (touchesBrand) {
    next.brand = { ...baselineBrand(props), ...p.brand };
  }
  if (touchesCues) {
    next.cues = applyCueOpsToList(props.cues, patches);
  }
  return next;
}

// ---------------------------------------------------------------------------
// Cue-text syntax — manual line breaks + /word/ emphasis
// ---------------------------------------------------------------------------
// The operator edits cue text with two pieces of syntax (spec §Operator
// additions #3): an explicit newline forces the two-line layout, and
// /word/ marks the gold em word. Internally the SRT convention is `\n` +
// <em>…</em>; these two helpers convert both ways for the inspector's
// text box. (Em renders gold on ONE-LINE cues; the two-line layout strips
// em by design — top is cream, punch line is uniformly gold.)

/** Storage → editor display: `<em c="#hex">word</em>` becomes `/word/{#hex}`
 *  (an operator-colored span) and plain `<em>word</em>` becomes `/word/`
 *  (the gold accent). Colored first so its span isn't caught by the plain
 *  rule. */
export function emToSlashes(text: string): string {
  return text
    .replace(/<em c="(#[0-9a-fA-F]{6})">([\s\S]*?)<\/em>/g, "/$2/{$1}")
    .replace(/<em>([\s\S]*?)<\/em>/g, "/$1/");
}

/** Editor display → storage: `/word/{#hex}` becomes `<em c="#hex">word</em>`
 *  and `/word/` becomes `<em>word</em>` (gold). Colored form is converted
 *  FIRST so the trailing `{#hex}` isn't stranded by the plain rule. Only
 *  same-line pairs convert; an unpaired `/` stays literal. */
export function slashesToEm(text: string): string {
  return text
    .replace(/\/([^/\n]+)\/\{(#[0-9a-fA-F]{6})\}/g, '<em c="$2">$1</em>')
    // The plain rule runs on the output of the colored rule, which now
    // contains `<em c="…">…</em>` tags whose `</em>` carries a `/`. Excluding
    // `<`/`>` from the inner class stops the scan bridging between two tags'
    // slashes and splicing garbage into the middle (critic-caught: a
    // color-FIRST two-span cue like `/a/{#111} /b/{#222}`).
    .replace(/\/([^/\n<>]+)\//g, "<em>$1</em>");
}
